// Roofing Industries EZI-FLO Multifit fascia presentation catalogue.
// Exact attachment, substrate and compatibility remain RI/installer decisions.

export type FasciaId = "multifit_136" | "multifit_150" | "multifit_185" | "multifit_190_external" | "multifit_190_internal";

export interface FasciaSpec {
  id: FasciaId;
  name: string;
  family: string;
  character: string;
  /** Overall folded section height used by the roof-edge presentation. */
  heightM: number;
  /** Published visible cover where RI provides it. */
  coverHeightM?: number;
  faceDepthM: number;
  attachment: "external" | "internal";
  geometryBasis: string;
  sourceUrl: string;
  sourceLabel: string;
}

export const FASCIAS: FasciaSpec[] = [
  {
    id: "multifit_136",
    name: "Multifit™ 136",
    family: "Compact external fascia",
    character: "A compact metal fascia that keeps the roof edge light.",
    heightM: 0.141,
    coverHeightM: 0.121,
    faceDepthM: 0.03,
    attachment: "external",
    geometryBasis: "RI published 141 mm overall section, 121 mm fascia cover and 30 mm return.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/136-fascia-and-accessories",
    sourceLabel: "Roofing Industries Multifit™ 136 fascia and accessories",
  },
  {
    id: "multifit_150",
    name: "Multifit™ 150",
    family: "Balanced external fascia",
    character: "A balanced metal fascia depth for a clean residential roof edge.",
    heightM: 0.15,
    coverHeightM: 0.13,
    faceDepthM: 0.03,
    attachment: "external",
    geometryBasis: "RI published 150 mm overall section, 130 mm fascia cover and 30 mm return.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/150-fascia-accessories",
    sourceLabel: "Roofing Industries Multifit™ 150 fascia and accessories",
  },
  {
    id: "multifit_185",
    name: "Multifit™ 185",
    family: "Deep external fascia",
    character: "A deeper continuous face that gives the eave a more substantial finish.",
    heightM: 0.188,
    coverHeightM: 0.168,
    faceDepthM: 0.03,
    attachment: "external",
    geometryBasis: "RI published 188 mm overall section, 168 mm fascia cover and 30 mm return.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/185-fascia-accessories",
    sourceLabel: "Roofing Industries Multifit™ 185 fascia and accessories",
  },
  {
    id: "multifit_190_external",
    name: "Multifit™ 190 External",
    family: "Deep external system",
    character: "A deep external fascia presentation for a strong, continuous roof edge.",
    heightM: 0.19,
    faceDepthM: 0.03,
    attachment: "external",
    geometryBasis: "190 mm RI product height; folded return is a labelled visual presentation.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/190-external",
    sourceLabel: "Roofing Industries Multifit™ 190 External fascia",
  },
  {
    id: "multifit_190_internal",
    name: "Multifit™ 190 Internal",
    family: "Concealed internal system",
    character: "A deep internal fascia arrangement for a restrained, integrated roof edge.",
    heightM: 0.19,
    faceDepthM: 0.03,
    attachment: "internal",
    geometryBasis: "190 mm RI product height; internal attachment detail remains subject to the selected gutter system.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/190-internal-fascia-accessories",
    sourceLabel: "Roofing Industries Multifit™ 190 Internal fascia",
  },
];

export const DEFAULT_FASCIA_ID: FasciaId = "multifit_150";

export function findFascia(id: string | undefined): FasciaSpec | undefined {
  return FASCIAS.find((fascia) => fascia.id === id);
}

export function fasciaCrossSection(fascia: FasciaSpec): Array<{ outwardM: number; verticalM: number }> {
  const depth = fascia.faceDepthM;
  const lowerReturn = fascia.attachment === "internal" ? depth * 0.58 : depth * 0.28;
  return [
    { outwardM: 0, verticalM: 0 },
    { outwardM: depth * 0.82, verticalM: -0.004 },
    { outwardM: depth, verticalM: -0.012 },
    { outwardM: depth, verticalM: -fascia.heightM * 0.38 },
    { outwardM: depth * 0.78, verticalM: -fascia.heightM * 0.42 },
    { outwardM: depth, verticalM: -fascia.heightM * 0.46 },
    { outwardM: depth, verticalM: -fascia.heightM + 0.014 },
    { outwardM: lowerReturn, verticalM: -fascia.heightM },
    { outwardM: 0, verticalM: -fascia.heightM + 0.014 },
  ];
}
